import type {
  DiagnosticReportSummary,
  DiagnosticSummary,
  DiagnosticManifest,
} from "./diagnostics";
import {
  normalizeReportList,
  normalizeSummary,
  normalizeManifest,
} from "./diagnostics";
import { accountApiBase, fetchWithConnectionAuth } from "../app/connection";

/** Same origin as the Run/Runner clients so the whole account surface points at
 *  one API. */
function defaultBaseUrl(): string {
  return accountApiBase();
}

export class DiagnosticsAuthError extends Error {
  readonly status = 401;
  constructor() {
    super("Diagnostics API: not signed in (401). Sign in to your Ato account.");
    this.name = "DiagnosticsAuthError";
  }
}

/** The report (or the whole diagnostics endpoint) does not exist. Used by the
 *  viewer to render "no diagnostics" quietly rather than as an error — the
 *  backend (ato-api#38) may not be deployed yet. */
export class DiagnosticsNotFoundError extends Error {
  readonly status = 404;
  constructor() {
    super("Diagnostics API: not found (404).");
    this.name = "DiagnosticsNotFoundError";
  }
}

export class DiagnosticsConnectionError extends Error {
  constructor(cause?: unknown) {
    super(
      `Diagnostics API: could not connect. ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = "DiagnosticsConnectionError";
  }
}

export class DiagnosticsApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "DiagnosticsApiError";
  }
}

/** A manifest fetch result: either fresh JSON + its ETag, or a 304 telling the
 *  caller its cached copy is still valid. */
export type ManifestResult =
  | { notModified: true }
  | { notModified: false; manifest: DiagnosticManifest; etag: string | null };

/**
 * Read-only client for the diagnostics surface (api.ato.run /v1/diagnostics/*).
 *
 * Boundary: cookie session (`credentials: "include"`), never a Runtime token,
 * never /v1/runtime/*. It only reads sealed reports; it has no upload methods
 * (the runner / ato-api own production). raw plaintext logs and crash dumps are
 * not exposed by any method here — only redacted summaries and the log chunks
 * the server chooses to serve.
 */
export class DiagnosticsApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: { baseUrl?: string; fetchImpl?: typeof fetch } = {}) {
    this.baseUrl = (config.baseUrl ?? defaultBaseUrl()).replace(/\/$/, "");
    this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
  }

  private async raw(
    path: string,
    headers: Record<string, string> = {},
  ): Promise<Response> {
    try {
      return await fetchWithConnectionAuth(`${this.baseUrl}${path}`, {
        method: "GET",
        headers,
      }, this.fetchImpl);
    } catch (err) {
      throw new DiagnosticsConnectionError(err);
    }
  }

  /** Throw the right typed error for a non-OK response; otherwise return it. */
  private async ensureOk(res: Response): Promise<Response> {
    if (res.status === 401) throw new DiagnosticsAuthError();
    if (res.status === 404) throw new DiagnosticsNotFoundError();
    if (!res.ok) {
      let code = "diagnostics_api_error";
      let message = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as Record<string, string> | undefined;
        code = j?.["code"] ?? j?.["error"] ?? code;
        message = j?.["message"] ?? message;
      } catch {
        /* non-JSON error body — keep the HTTP status message */
      }
      throw new DiagnosticsApiError(res.status, code, message);
    }
    return res;
  }

  private async json<T>(path: string): Promise<unknown> {
    const res = await this.ensureOk(await this.raw(path));
    try {
      return (await res.json()) as T;
    } catch (err) {
      throw new DiagnosticsConnectionError(err);
    }
  }

  /** GET /v1/diagnostics/reports?run_id=… — the list (D1 index only). The
   *  arg is the run id (the canonical join key), sent as `run_id`. */
  async listReports(runId: string): Promise<DiagnosticReportSummary[]> {
    const qs = new URLSearchParams({ run_id: runId }).toString();
    return normalizeReportList(await this.json(`/v1/diagnostics/reports?${qs}`));
  }

  /** GET /v1/diagnostics/reports/:id — the redacted summary (loaded first). */
  async getSummary(reportId: string): Promise<DiagnosticSummary> {
    return normalizeSummary(
      await this.json(`/v1/diagnostics/reports/${encodeURIComponent(reportId)}`),
    );
  }

  /**
   * GET /v1/diagnostics/reports/:id/manifest — conditional on `etag`. A 304
   * returns `{ notModified: true }` so the caller keeps its cached manifest.
   */
  async getManifest(reportId: string, etag?: string | null): Promise<ManifestResult> {
    const headers: Record<string, string> = {};
    if (etag) headers["If-None-Match"] = etag;
    const res = await this.raw(
      `/v1/diagnostics/reports/${encodeURIComponent(reportId)}/manifest`,
      headers,
    );
    if (res.status === 304) return { notModified: true };
    await this.ensureOk(res);
    let body: unknown;
    try {
      body = await res.json();
    } catch (err) {
      throw new DiagnosticsConnectionError(err);
    }
    return {
      notModified: false,
      manifest: normalizeManifest(body),
      etag: res.headers?.get?.("ETag") ?? null,
    };
  }

  /**
   * GET /v1/diagnostics/reports/:id/chunks/:chunkId — the chunk body as text.
   * Chunks are content-addressed (`chunk_id` === sha256) and immutable; callers
   * cache by chunk_id and never re-fetch a hit. (zstd transport decode, when the
   * server stops sending identity/gzip, lands in Phase 2.)
   */
  async getChunkText(reportId: string, chunkId: string): Promise<string> {
    const res = await this.ensureOk(
      await this.raw(
        `/v1/diagnostics/reports/${encodeURIComponent(reportId)}/chunks/${encodeURIComponent(chunkId)}`,
      ),
    );
    try {
      return await res.text();
    } catch (err) {
      throw new DiagnosticsConnectionError(err);
    }
  }
}
